/**
 * Container packing async thunks for PackingV2
 *
 * Endpoints:
 * - Container Scan:           POST /rest/handheld/pack/shipment/{shipmentId}/container/scan
 * - Bulk Create:              POST /rest/handheld/pack/shipment/{shipmentId}/container/bulk
 * - Create Empty Container:   POST /rest/handheld/pack/shipment/{shipmentId}/container/create
 * - Close Container:          POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/close
 * - Reopen Container:         POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/reopen
 * - Reset Container:          POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/reset
 * - Reset & Return Container: POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/resetAndReturn
 * - Delete Container:         DELETE /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { message } from 'antd';
import { ContainerScanActionLabels, } from '../../components/packing_v2/types';
import { extractErrorMessage, emitPackingPerf, nowMs } from './packingThunkUtils';
import { fetchShipmentDetails } from './packingThunks';
// Helper to get auth headers
var getAuthHeaders = function () {
    var moquiSessionToken = localStorage.getItem('moquiSessionToken');
    return moquiSessionToken ? { moquiSessionToken: moquiSessionToken } : {};
};
// After any container mutation, do a full shipment refresh (includes containers).
var refreshAfterContainerMutation = function (dispatch, shipmentId, _mutation) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, dispatch(fetchShipmentDetails({ shipmentId: shipmentId, silent: true }))];
            case 1:
                _a.sent();
                return [2 /*return*/];
        }
    });
}); };
export var containerScan = createAsyncThunk('packing/containerScan', function (_a, _b) {
    var shipmentId = _a.shipmentId, scanInput = _a.scanInput, packIntoContainerId = _a.packIntoContainerId, parentContainerId = _a.parentContainerId, containerTypeEnumId = _a.containerTypeEnumId, _c = _a.quantity, quantity = _c === void 0 ? 1 : _c, serialNumber = _a.serialNumber;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, data, actionLabel, error_1, errorMsg;
        var _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerScan',
                        shipmentId: shipmentId
                    });
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/scan", __assign(__assign(__assign(__assign(__assign({ scanInput: scanInput }, (packIntoContainerId ? { packIntoContainerId: packIntoContainerId } : {})), (parentContainerId ? { parentContainerId: parentContainerId } : {})), (containerTypeEnumId ? { containerTypeEnumId: containerTypeEnumId } : {})), { quantity: quantity }), (serialNumber ? { serialNumber: serialNumber } : {})), { headers: getAuthHeaders() })];
                case 2:
                    response = _e.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    data = response.data;
                    actionLabel = (_d = ContainerScanActionLabels[data.actionType]) !== null && _d !== void 0 ? _d : String(data.actionType);
                    message.success("Scan processed: " + actionLabel);
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerScan')];
                case 3:
                    _e.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerScan',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, data];
                case 4: return [2 /*return*/, rejectWithValue('Container scan failed')];
                case 5:
                    error_1 = _e.sent();
                    errorMsg = extractErrorMessage(error_1, 'Failed to process container scan');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerScan',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
export var containerBulkCreate = createAsyncThunk('packing/containerBulkCreate', function (_a, _b) {
    var shipmentId = _a.shipmentId, scanInput = _a.scanInput, numberOfContainers = _a.numberOfContainers, quantityPerContainer = _a.quantityPerContainer, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId, packIntoContainerId = _a.packIntoContainerId, containerTypeEnumId = _a.containerTypeEnumId, createPackIntoContainer = _a.createPackIntoContainer, packIntoContainerTypeEnumId = _a.packIntoContainerTypeEnumId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_2, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerBulkCreate',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/bulk", __assign(__assign(__assign({ scanInput: scanInput, numberOfContainers: numberOfContainers, quantityPerContainer: quantityPerContainer, shipmentBoxTypeId: shipmentBoxTypeId, weight: weight, weightUomId: weightUomId }, (packIntoContainerId ? { packIntoContainerId: packIntoContainerId } : {})), (containerTypeEnumId ? { containerTypeEnumId: containerTypeEnumId } : {})), (createPackIntoContainer ? { createPackIntoContainer: createPackIntoContainer, packIntoContainerTypeEnumId: packIntoContainerTypeEnumId } : {})), { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success(numberOfContainers + " containers created successfully");
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerBulkCreate')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerBulkCreate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Bulk container creation failed')];
                case 5:
                    error_2 = _c.sent();
                    errorMsg = extractErrorMessage(error_2, 'Failed to create containers');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerBulkCreate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
export var containerCreate = createAsyncThunk('packing/containerCreate', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerTypeEnumId = _a.containerTypeEnumId, parentContainerId = _a.parentContainerId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, requestBody, response, error_3, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerCreate',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    requestBody = __assign({ containerTypeEnumId: containerTypeEnumId }, (parentContainerId ? { parentContainerId: parentContainerId } : {}));
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container", requestBody, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container created');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerCreate')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerCreate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Create container failed')];
                case 5:
                    error_3 = _c.sent();
                    errorMsg = extractErrorMessage(error_3, 'Failed to create container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerCreate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerClose
// POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/close
// Service: mantle.shipment.ShipmentContainerServices.close#ShipmentContainerToPackage
// =============================================================================
export var containerClose = createAsyncThunk('packing/containerClose', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_4, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerClose',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId + "/close", { shipmentBoxTypeId: shipmentBoxTypeId, weight: weight, weightUomId: weightUomId }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container closed');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerClose')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerClose',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Close container failed')];
                case 5:
                    error_4 = _c.sent();
                    errorMsg = extractErrorMessage(error_4, 'Failed to close container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerClose',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
export var containerBulkClose = createAsyncThunk('packing/containerBulkClose', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerIds = _a.containerIds, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_5, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerBulkClose',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/bulk/close", { containerIds: containerIds, shipmentBoxTypeId: shipmentBoxTypeId, weight: weight, weightUomId: weightUomId }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Containers closed');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerBulkClose')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerBulkClose',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Bulk close container failed')];
                case 5:
                    error_5 = _c.sent();
                    errorMsg = extractErrorMessage(error_5, 'Failed to bulk close containers');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerBulkClose',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerReopen
// POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/reopen
// Service: mantle.shipment.ShipmentContainerServices.reopen#ShipmentContainerPackage
// =============================================================================
export var containerReopen = createAsyncThunk('packing/containerReopen', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_6, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerReopen',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId + "/reopen", {}, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container reopened');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerReopen')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerReopen',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Reopen container failed')];
                case 5:
                    error_6 = _c.sent();
                    errorMsg = extractErrorMessage(error_6, 'Failed to reopen container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerReopen',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerDelete
// DELETE /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}
// Service: mantle.shipment.ShipmentContainerServices.delete#ShipmentContainer
// =============================================================================
export var containerDelete = createAsyncThunk('packing/containerDelete', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_7, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerDelete',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.delete("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container deleted');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerDelete')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerDelete',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Delete container failed')];
                case 5:
                    error_7 = _c.sent();
                    errorMsg = extractErrorMessage(error_7, 'Failed to delete container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerDelete',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerReset
// POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/reset
// Service: handheld.shipment.ShipmentContainerServices.reset#ShipmentContainer
// Unpacks all items from the container without deleting the container itself.
// =============================================================================
export var containerReset = createAsyncThunk('packing/containerReset', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_8, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerReset',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId + "/reset", {}, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container reset');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerReset')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerReset',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Reset container failed')];
                case 5:
                    error_8 = _c.sent();
                    errorMsg = extractErrorMessage(error_8, 'Failed to reset container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerReset',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerResetAndReturn
// POST /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/resetAndReturn
// Service: handheld.shipment.ShipmentContainerServices.resetAndReturn#ShipmentContainer
//
// Used for ADOPTED containers only. Unpacks the shipment items, removes the
// container from the shipment, and leaves the warehouse container + assets in
// place. The backend fails normal reset/delete on adopted containers
// =============================================================================
export var containerResetAndReturn = createAsyncThunk('packing/containerResetAndReturn', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_9, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerResetAndReturn',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.post("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId + "/resetAndReturn", {}, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container returned to inventory');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerResetAndReturn')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerResetAndReturn',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Reset and return container failed')];
                case 5:
                    error_9 = _c.sent();
                    errorMsg = extractErrorMessage(error_9, 'Failed to return container to inventory');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerResetAndReturn',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerUpdate
// PATCH /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}
// Updates box type and/or weight of an open container.
// =============================================================================
export var containerUpdate = createAsyncThunk('packing/containerUpdate', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId, shipmentBoxTypeId = _a.shipmentBoxTypeId, weight = _a.weight, weightUomId = _a.weightUomId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_10, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerUpdate',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.patch("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId, __assign(__assign(__assign({}, (shipmentBoxTypeId !== undefined ? { shipmentBoxTypeId: shipmentBoxTypeId } : {})), (weight !== undefined ? { weight: weight } : {})), (weightUomId !== undefined ? { weightUomId: weightUomId } : {})), { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container updated');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerUpdate')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerUpdate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Update container failed')];
                case 5:
                    error_10 = _c.sent();
                    errorMsg = extractErrorMessage(error_10, 'Failed to update container');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerUpdate',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
// =============================================================================
// containerUpdateType
// PUT /rest/handheld/pack/shipment/{shipmentId}/container/{containerId}/type
// Service: mantle.shipment.ShipmentContainerServices.update#ShipmentContainerType
// =============================================================================
export var containerUpdateType = createAsyncThunk('packing/containerUpdateType', function (_a, _b) {
    var shipmentId = _a.shipmentId, containerId = _a.containerId, containerTypeEnumId = _a.containerTypeEnumId;
    var rejectWithValue = _b.rejectWithValue, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var mutationStartedAt, response, error_11, errorMsg;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    mutationStartedAt = nowMs();
                    emitPackingPerf('packing_mutation.started', {
                        mutation: 'containerUpdateType',
                        shipmentId: shipmentId
                    });
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, axios.put("/rest/s1/handheld/pack/shipment/" + shipmentId + "/container/" + containerId + "/type", { containerTypeEnumId: containerTypeEnumId }, { headers: getAuthHeaders() })];
                case 2:
                    response = _c.sent();
                    if (!(response.status === 200)) return [3 /*break*/, 4];
                    message.success('Container type updated');
                    return [4 /*yield*/, refreshAfterContainerMutation(dispatch, shipmentId, 'containerUpdateType')];
                case 3:
                    _c.sent();
                    emitPackingPerf('packing_mutation.completed', {
                        mutation: 'containerUpdateType',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1))
                    });
                    return [2 /*return*/, response.data];
                case 4: return [2 /*return*/, rejectWithValue('Update container type failed')];
                case 5:
                    error_11 = _c.sent();
                    errorMsg = extractErrorMessage(error_11, 'Failed to update container type');
                    message.error(errorMsg);
                    emitPackingPerf('packing_mutation.failed', {
                        mutation: 'containerUpdateType',
                        shipmentId: shipmentId,
                        durationMs: Number((nowMs() - mutationStartedAt).toFixed(1)),
                        error: errorMsg
                    });
                    return [2 /*return*/, rejectWithValue(errorMsg)];
                case 6: return [2 /*return*/];
            }
        });
    });
});
